/**
 * storeCommandHandlers — wiring real dos comandos nas stores/serviços do player.
 * =============================================================================
 *
 * Adapter concreto de {@link EmbeddedCommandHandlers}. As dependências são
 * INJETADAS (DI) → o adapter fica testável com mocks e o bootstrap liga os
 * singletons reais (streamStore, volumeService, platform, window).
 *
 * Fase 2 = plano de controle. O wiring usa métodos JÁ existentes (sem mudar
 * comportamento compartilhado):
 * - `setVolume` → volumeService.setVolume
 * - `hide`/`unhide` → streamStore.setPlaying(false/true) + platform.onHide/onResume (D2:
 *   pausa o decode e congela; retoma do ponto). A supressão fina de impressão/analytics
 *   e o "stop clock" são aprofundados nas fases seguintes — aqui pausa o playback.
 * - `stop`/`start` → pausa/retoma o loop (o sync já auto-inicia no boot do app;
 *   `tenant`/`apiUrl` chegam ANTES, pelo marcador que o AAR injeta em
 *   document-start — vide `hostConfig.ts`). `start(config).tags`
 *   (Fase 4a/§16) → `setHostTags` → sync → admin (tags `source='host'`).
 * - `configure` → aplica o que dá pra aplicar sem reiniciar (volume); demais campos
 *   nas fases seguintes.
 * - `reload` → window.location.reload (único reinício real, §5).
 */
import { EmbeddedCommandHandlers } from './commandHandlers';
import { markHostVolume, releaseHostVolume } from './hostVolumeControl';
import { hostApiUrl } from './hostConfig';
import { ConfigurePayload, SetVolumePayload, StartPayload } from './protocol';

export interface StoreCommandDeps {
  /** streamStore.setHostHidden — congela/retoma o playback de verdade (D2). */
  setHostHidden: (hidden: boolean) => void;
  /** volumeService.setVolume (0-100) */
  setVolume: (level: number) => void | Promise<void>;
  /** platform.onHide */
  onHide: () => void;
  /** platform.onResume */
  onResume: () => void;
  /** window.location.reload */
  reload: () => void;
  /**
   * syncStore.setHostTags — tags do host (Fase 4a/§16). O player envia no sync
   * e o server reconcilia com `source='host'`. `[]` explícito = limpar.
   */
  setHostTags: (tags: string[]) => void;
  /**
   * Volume da config do servidor persistida (0-100) ou undefined. Usado no
   * `releaseVolume` pra reaplicar o volume do servidor ao devolver o controle.
   */
  getServerVolume: () => number | undefined;
  /**
   * Notifica o host (evento `volumeChanged`) o volume efetivo + quem controla.
   * O host exibe o valor atual sem consultar.
   */
  emitVolumeChanged: (level: number, source: 'server' | 'host') => void;
  /**
   * Path 3 — captura o frame atual (DOM+vídeo) e o entrega ao host
   * (`window.EffluxHost.onCoverFrame`). O bootstrap injeta a implementação real
   * (usa `captureScreenBase64` + a bridge nativa). Best-effort: em falha, entrega
   * string vazia → o host recria a WebView sem cover (breve preto, sem crash).
   */
  captureCoverFrame: () => void | Promise<void>;
}

export function createStoreCommandHandlers(
  deps: StoreCommandDeps,
): EmbeddedCommandHandlers {
  return {
    start(payload: StartPayload): void {
      // Retoma o playback (o sync já auto-inicia no boot).
      //
      // `apiUrl`/`tenant` NÃO são aplicados aqui, e não é omissão: quando este
      // comando chega, o bundle já bootou e o primeiro sync já saiu. Quem os
      // aplica é o AAR, escrevendo `window.__effluxEmbedded` em document-start —
      // antes do primeiro script — e recarregando a WebView se o host mandar um
      // endereço diferente do que o bundle carregou. Vide `hostConfig.ts`.
      //
      // O aviso abaixo existe porque essa divergência só é visível daqui: se ela
      // persistir, o reload do AAR não aconteceu e o player está falando com o
      // servidor errado em silêncio — que foi exatamente o bug de 2026-09-22.
      const doHost = hostApiUrl();
      const pedido = payload?.apiUrl?.trim().replace(/\/+$/, '');
      if (pedido && pedido !== doHost) {
        console.warn(
          `[Embedded] start pediu apiUrl="${pedido}" mas o bundle carregou com ` +
            `"${doHost ?? '(build)'}" — esperando o reload do AAR.`,
        );
      }
      deps.setHostHidden(false);
      // Fase 4a/§16 — tags do host. Só toca quando o host mandou `tags`
      // EXPLICITAMENTE (undefined = não mexe; `[]` = limpar). Assim um start
      // sem tags nunca apaga as que o host já tinha setado.
      if (payload?.tags !== undefined) {
        deps.setHostTags(Array.isArray(payload.tags) ? payload.tags : []);
      }
    },

    stop(): void {
      // Congela o playback (pausa o <video>); WebView e estado permanecem.
      deps.setHostHidden(true);
    },

    configure(payload: ConfigurePayload): void {
      // Aplica sem reiniciar. Volume agora; brilho/interactive nas fases seguintes.
      if (typeof payload?.volumeLevel === 'number') {
        void deps.setVolume(payload.volumeLevel);
      }
    },

    hide(): void {
      // D2 — congela: pausa o <video> (setHostHidden) e sinaliza à plataforma.
      deps.setHostHidden(true);
      deps.onHide();
    },

    unhide(): void {
      // D2 — retoma do ponto congelado.
      deps.onResume();
      deps.setHostHidden(false);
    },

    setVolume(payload: SetVolumePayload): void {
      if (typeof payload?.level === 'number') {
        // Host-owned volume (regra B2B): a partir daqui o HOST detém o controle
        // — o ConfigManager para de aplicar o volume do servidor até releaseVolume.
        markHostVolume(payload.level);
        void deps.setVolume(payload.level);
        deps.emitVolumeChanged(payload.level, 'host');
      }
    },

    releaseVolume(): void {
      // Host devolve o controle ao servidor Efflux. Limpa o override sticky e
      // reaplica JÁ o volume da config do servidor (efeito imediato), avisando o
      // host do novo valor efetivo (server no controle).
      releaseHostVolume();
      const serverLevel = deps.getServerVolume();
      if (typeof serverLevel === 'number') {
        void deps.setVolume(serverLevel);
        deps.emitVolumeChanged(serverLevel, 'server');
      }
    },

    reload(): void {
      deps.reload();
    },

    captureCoverFrame(): void | Promise<void> {
      // Path 3 — delega ao bootstrap (captura + bridge nativa vivem lá, onde
      // `window`/`captureScreenBase64` estão disponíveis).
      return deps.captureCoverFrame();
    },
  };
}
